import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Tooltip } from '@cimpress/react-components';
import GenericSelector, { IChangeMetadata } from '@cimpress-technology/generic-selector';
import type {
    IDoublePivotStateDescriptor,
    ISinglePivotStateDescriptor,
    IStateDescriptor,
} from '@cimpress-technology/generic-selector/lib/Models/StateDescriptor';
import auth from 'src/auth';
import { isEqual } from 'lodash';

type SelectionState = IStateDescriptor | ISinglePivotStateDescriptor | IDoublePivotStateDescriptor;

interface Props {
    sku?: string;
    skuVersion?: number;
    attributes: Record<string, any>;
    onChange?: (updates: Record<string, any>) => void;
}

const ProductSelector = (props: Props) => {
    const [attributesTooltipOpen, setAttributesToolTipOpen] = useState(false);
    const [displayabledAttributes, setDisplayabledAttributes] = useState<string[]>([]);
    const keyAttributes = useMemo(() => Object.keys(props.attributes), [props.attributes]);
    const [interimSelections, setInterimSelections] = useState(keyAttributes);

    const checkboxToggle = (attributeKey: string) => {
        const index = interimSelections.findIndex((each) => each === attributeKey);
        if (index >= 0) {
            setInterimSelections([...interimSelections.slice(0, index), ...interimSelections.slice(index + 1)]);
        } else {
            setInterimSelections([...interimSelections, attributeKey]);
        }
    };
    const closeAttributeSelector = () => {
        if (!isEqual(interimSelections, keyAttributes)) {
            const updates: Record<string, any> = {};
            interimSelections.forEach((each) => {
                updates[each] = props.attributes[each];
            });
            props.onChange && props.onChange(updates);
        }
        setAttributesToolTipOpen(false);
    };
    const attributeConfigurations = useMemo(() => {
        const attributes: Record<string, { initialSelection: any; isHidden: boolean }> = {};
        displayabledAttributes.forEach((key) => {
            attributes[key] = {
                initialSelection: props.attributes[key],
                isHidden: !keyAttributes.includes(key),
            };
        });
        return attributes;
    }, [keyAttributes, displayabledAttributes, props.attributes]);

    const onLoad = (state: SelectionState) => {
        // This selector is never configured with pivot attributes here, so `state` is always the
        // plain `IStateDescriptor` shape rather than one of the pivot variants.
        const { attributes } = state as IStateDescriptor;
        const temp = attributes.filter((attr) => attr.isDisplayed).map((attri) => attri.key);
        setDisplayabledAttributes(temp);
    };
    const handleAttributeChanges = (changes: SelectionState, _metadata: IChangeMetadata) => {
        // Same reasoning as `onLoad` above -- always the plain `IStateDescriptor` shape here.
        const { attributes } = changes as IStateDescriptor;
        const updates: Record<string, any> = {};
        keyAttributes.forEach((each) => {
            const attributeDetails = attributes.find((eachAttribute) => eachAttribute.key === each);
            if (attributeDetails) {
                updates[each] = attributeDetails.resolvedValue;
            }
        });
        props.onChange && props.onChange(updates);
    };

    return (
        <div>
            <GenericSelector
                authToken={auth.getAccessToken() || ''}
                productId={props.sku}
                // `productVersion` is typed as a string, but this always receives the numeric
                // `skuVersion` from redux state; cast rather than stringify to avoid changing the
                // value actually passed through.
                productVersion={props.skuVersion as unknown as string}
                attributeConfigurations={attributeConfigurations}
                errorAlertConfiguration={{ showErrorAlert: false }}
                onLoad={onLoad}
                isColorSwatch={true}
                onChange={handleAttributeChanges}
            />
            <Tooltip
                tooltipStyle={{ maxWidth: '800px' }}
                direction='bottom'
                show={attributesTooltipOpen}
                variety='popover'
                contents={
                    <div style={{ maxWidth: '500px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            {displayabledAttributes?.map((key) => (
                                <Checkbox
                                    data-testid={`variable-selector_${key}`}
                                    key={key}
                                    label={key}
                                    style={{ width: '33%' }}
                                    checked={interimSelections.includes(key)}
                                    onChange={() => checkboxToggle(key)}
                                />
                            ))}
                        </div>
                        <Button
                            data-testid='variable-selector_close_button'
                            variant='primary'
                            className='variables__confirm-button'
                            onClick={closeAttributeSelector}>
                            Ok
                        </Button>
                    </div>
                }
            />
            <Button
                data-testid='variable-selector_add_remove_button'
                variant='link'
                onClick={() => {
                    setAttributesToolTipOpen(!attributesTooltipOpen);
                }}>
                Add/Remove Attributes
            </Button>
        </div>
    );
};

export default ProductSelector;
